// Station detail views for R2: lightweight single-line diagram (no external dep),
// inverter matrix, per-inverter string grid, КТП panel.
import i18n from '@/i18n';
import { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { scada, stateColor, STATE_LABEL, type OpState } from '@/scada/tokens';
import type { StationConfig } from '@/scada/mock/stations';
import type { StationTelemetry } from '@/scada/mock/telemetry';
import type { StationDetail, InverterUnit } from '@/scada/mock/stationDetail';

const fmtKw = (kw: number) => (kw >= 1000 ? `${(kw / 1000).toFixed(2)} ${i18n.t('ak_scada2_mw')}` : `${Math.round(kw)} ${i18n.t('ak_scada2_kw')}`);
const dot = (s: OpState) => <span style={{ width: 8, height: 8, borderRadius: 999, background: stateColor(s), display: 'inline-block' }} />;

/**
 * Картка вузла однолінійної схеми — той самий каркас, що в референсі
 * (велика назва, рядки показників під роздільником, за потреби велике
 * число або шкала навантаження, статус-рядок із крапкою знизу), але БЕЗ
 * стороннього шрифту й кольору: типографіка — `font-display` (Urbanist,
 * той самий, що й решта кабінету), кольори — виключно `scada.*` (=
 * ті самі `var(--…)`, що на будь-якій іншій сторінці), іконки лишились
 * Remix (як і раніше в цьому файлі) — це не нова правка, зміни не чіпають
 * набір іконок.
 *
 * `subtitle` і `load` необов'язкові: підпис під назвою й шкала
 * навантаження показуються, лише коли є реальні дані (моки СЕС не дають
 * фідера чи паспортного струму на КТП, тож придумувати ці цифри тут не
 * стали — порожня картка чеснiша за фіктивне число).
 */
function Node({ title, subtitle, lines, state, icon, big, load }: {
  title: string; subtitle?: string; lines: { k: string; v: string }[]; state: OpState; icon: string;
  big?: { value: string; unit: string }; load?: number;
}) {
  const c = stateColor(state);
  return (
    <div style={{
      background: scada.panel2, border: `1.5px solid ${c}66`, borderRadius: 10,
      padding: '12px 14px 11px', minWidth: 168, boxShadow: 'var(--shadow)',
    }}>
      <div className="flex items-center" style={{ gap: 7 }}>
        <i className={icon} style={{ color: c, fontSize: 13 }} />
        <span className="font-display" style={{ fontWeight: 700, fontSize: 15, letterSpacing: '.02em', textTransform: 'uppercase', color: scada.text, lineHeight: 1.15 }}>{title}</span>
      </div>
      {subtitle && <div style={{ marginTop: 1, fontSize: 10.5, color: scada.textMuted }}>{subtitle}</div>}

      {big && (
        <div className="flex items-baseline" style={{ gap: 6, marginTop: 8 }}>
          <span className="font-display tabular-nums" style={{ fontSize: 27, fontWeight: 700, lineHeight: 1, color: scada.text }}>{big.value}</span>
          <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: scada.textMuted }}>{big.unit}</span>
        </div>
      )}

      <div style={{ marginTop: 9, display: 'grid', gap: 3, borderTop: `1px solid ${scada.border}`, paddingTop: 8 }}>
        {lines.map((l) => (
          <div key={l.k} className="flex items-baseline justify-between" style={{ gap: 10 }}>
            <span style={{ fontSize: 10.5, color: scada.textMuted, whiteSpace: 'nowrap' }}>{l.k}</span>
            <span className="tabular-nums" style={{ fontSize: 12, fontWeight: 500, color: scada.textDim, whiteSpace: 'nowrap' }}>{l.v}</span>
          </div>
        ))}
      </div>

      {load != null && (
        <div style={{ marginTop: 10 }}>
          <div className="flex items-baseline justify-between" style={{ marginBottom: 4 }}>
            <span style={{ fontSize: 10.5, color: scada.textMuted }}>{i18n.t('ak_scada2_group_load')}</span>
            <span className="tabular-nums" style={{ fontSize: 12.5, fontWeight: 600, color: scada.text }}>{Math.round(load)}%</span>
          </div>
          <div style={{ height: 5, borderRadius: 3, background: scada.border, overflow: 'hidden', position: 'relative' }}>
            <div style={{ position: 'absolute', inset: 0, width: `${load}%`, borderRadius: 3, background: c }} />
          </div>
        </div>
      )}

      <div className="flex items-center" style={{ gap: 7, marginTop: 10, paddingTop: 8, borderTop: `1px solid ${scada.border}` }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: c, boxShadow: `0 0 0 3px ${c}33`, flexShrink: 0 }} />
        <span className="font-display" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: c }}>{STATE_LABEL[state]}</span>
      </div>
    </div>
  );
}

// Пряме («елбоу») з'єднання без кривих. Схема тепер компактна: інвертори й
// КТП стоять КОЛОНКАМИ (інвертор зверху, його КТП знизу, як у першій
// версії), точка приєднання — праворуч від усіх колонок. Тому два типи
// зв'язку: inv→свій КТП — вертикальний (та сама колонка, різний Y, бере
// midY, коли X трохи розходиться через різну ширину карток); КТП→хаб —
// горизонтальний (різні X і Y, бере midX).
function elbowV(from: { x: number; y: number }, to: { x: number; y: number }): string {
  if (Math.abs(from.x - to.x) < 0.5) return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  const midY = from.y + (to.y - from.y) / 2;
  return `M ${from.x} ${from.y} L ${from.x} ${midY} L ${to.x} ${midY} L ${to.x} ${to.y}`;
}
// КТП/BESS → хаб: колонки стоять поруч, тож пряма горизонталь від НЕ
// крайньої колонки фізично перетинала б картки колонок, що стоять правіше
// (усі вони на одному Y). Замість цього — маршрут шиною: вниз до спільної
// горизонталі ПІД усім рядом карток (де точно порожньо), уздовж неї до X
// хаба, і вгору в хаб знизу. Класична розводка однолінійних схем.
function busUp(from: { x: number; y: number }, to: { x: number; y: number }, trunkY: number): string {
  return `M ${from.x} ${from.y} L ${from.x} ${trunkY} L ${to.x} ${trunkY} L ${to.x} ${to.y}`;
}

// ─── Single-line diagram ─────────────────────────────────────────────────────
// Геометрія рахується з реальних позицій карток (як і в паперовому референсі),
// тож при перебудові сітки на вузькому екрані лінії перемальовуються самі.
// Колір — токени кабінету (scada.*, ті самі, що й на решті сторінки): зелений
// у стані спокою (§3, під напругою/норма), бурштиновий (--warn) — коли гілка
// вибрана курсором. Лінії йдуть ВІД джерела (інвертор/КТП) ДО точки
// приєднання — і геометрично, і в напрямку анімації «потоку», відповідно до
// реального напряму передачі: генерація → мережа.
export function Sld({ cfg, t, detail }: { cfg: StationConfig; t: StationTelemetry; detail: StationDetail }) {
  // treeRef - зовнішній, НЕ масштабований контейнер: за його clientWidth
  // рахуємо, скільки місця реально є. canvasRef - внутрішній шар з усіма
  // картками й SVG, який стискаємо transform:scale, коли природна ширина
  // (canvasRef.scrollWidth - CSS-трансформ на неї не впливає) не влазить.
  const treeRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const hubRef = useRef<HTMLDivElement | null>(null);
  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [paths, setPaths] = useState<{ owner: string; d: string }[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [fit, setFit] = useState({ scale: 1, w: 0, h: 0 });

  const setNodeRef = (id: string) => (el: HTMLDivElement | null) => { nodeRefs.current[id] = el; };

  // Природний (немасштабований) розмір схеми проти доступної ширини плитки.
  // Зменшуємо ЛИШЕ коли не влазить (scale ніколи не буває > 1) - на широких
  // екранах схема лишається в натуральну величину.
  const fitToWidth = useCallback(() => {
    const outer = treeRef.current;
    const canvas = canvasRef.current;
    if (!outer || !canvas) return;
    const naturalW = canvas.scrollWidth;
    const naturalH = canvas.scrollHeight;
    const avail = outer.clientWidth;
    const next = naturalW > avail && naturalW > 0 ? Math.max(0.5, avail / naturalW) : 1;
    setFit((prev) => (Math.abs(prev.scale - next) > 0.005 || prev.w !== naturalW || prev.h !== naturalH
      ? { scale: next, w: naturalW, h: naturalH } : prev));
  }, []);

  const draw = useCallback(() => {
    const container = canvasRef.current;
    const hub = hubRef.current;
    if (!container || !hub) return;
    const base = container.getBoundingClientRect();
    const scale = fit.scale || 1;
    // getBoundingClientRect віддає РЕАЛЬНІ (вже масштабовані) екранні пікселі;
    // ділимо на scale, щоб повернутись у природну систему координат SVG -
    // САМЕ вона потім масштабується разом з картками тим самим CSS transform.
    const rectIn = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      return { x: (r.left - base.left) / scale, y: (r.top - base.top) / scale, w: r.width / scale, h: r.height / scale };
    };
    const h = rectIn(hub);
    // Хаб приймає знизу (шина заходить у нижню грань, не в лівий бік —
    // інакше довелось би повертати трохи вгору над рядом карток).
    const to = { x: h.x + h.w / 2, y: h.y + h.h };
    const next: { owner: string; d: string }[] = [];

    // Спільна горизонталь ПІД усім рядом карток (КТП + BESS + сам хаб,
    // якщо він нижчий за них) — найнижча грань серед усіх + невеликий відступ,
    // щоб шина не торкалась жодної картки.
    let trunkY = h.y + h.h;
    detail.ktps.forEach((k) => {
      const ktpEl = nodeRefs.current[k.id];
      if (ktpEl) trunkY = Math.max(trunkY, rectIn(ktpEl).y + rectIn(ktpEl).h);
    });
    if (cfg.hasBess && t.bess) {
      const bessEl = nodeRefs.current['bess'];
      if (bessEl) trunkY = Math.max(trunkY, rectIn(bessEl).y + rectIn(bessEl).h);
    }
    trunkY += 18;

    detail.ktps.forEach((k) => {
      const ktpEl = nodeRefs.current[k.id];
      const invEl = nodeRefs.current[`${k.id}-inv`];
      if (!ktpEl || !invEl) return;
      const kr = rectIn(ktpEl);
      const vr = rectIn(invEl);
      // Інвертор зверху → його КТП знизу, та сама колонка: низ інвертора → верх КТП.
      const vBottom = { x: vr.x + vr.w / 2, y: vr.y + vr.h };
      const kTop = { x: kr.x + kr.w / 2, y: kr.y };
      const kBottom = { x: kr.x + kr.w / 2, y: kr.y + kr.h };
      next.push({ owner: k.id, d: elbowV(vBottom, kTop) });
      next.push({ owner: k.id, d: busUp(kBottom, to, trunkY) });
    });

    if (cfg.hasBess && t.bess) {
      const bessEl = nodeRefs.current['bess'];
      if (bessEl) {
        const br = rectIn(bessEl);
        const bBottom = { x: br.x + br.w / 2, y: br.y + br.h };
        next.push({ owner: 'bess', d: busUp(bBottom, to, trunkY) });
      }
    }

    setPaths(next);
  }, [detail.ktps, cfg.hasBess, t.bess]);

  // Спершу приміряти масштаб (природний розмір карток ще не залежить від
  // scale), потім перерахувати лінії - вони мають лягти вже в масштабовану
  // систему координат.
  useLayoutEffect(() => { fitToWidth(); }, [fitToWidth]);
  useLayoutEffect(() => { draw(); }, [draw]);
  useEffect(() => {
    const el = treeRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { fitToWidth(); draw(); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [draw, fitToWidth]);

  const bigVal = t.activePowerKw >= 1000 ? (t.activePowerKw / 1000).toFixed(2) : Math.round(t.activePowerKw).toString();
  const bigUnit = t.activePowerKw >= 1000 ? i18n.t('ak_scada2_mw') : i18n.t('ak_scada2_kw');

  return (
    // paddingBottom - мiсце пiд шинну горизонталь (trunkY = нижня грань
    // карток + 18px): без цього контейнер (i SVG всередину нього) обривався
    // рiвно по нижньому краю карток, i лiнiї до хаба просто зникали пiд обрiз.
    //
    // Зовнiшнiй treeRef БЕЗ overflow-x:auto й БЕЗ transform - лише вимiрює
    // доступну ширину. Висота обгортки = природна висота * scale, iнакше пiд
    // зменшеною схемою лишався б порожнiй простiр (transform не змiнює layout
    // box, лише те, як вiн МАЛЮЄТЬСЯ).
    <div ref={treeRef} style={{ position: 'relative', padding: '8px 4px', width: '100%' }}>
      <div style={{ height: fit.h ? fit.h * fit.scale : undefined, overflow: 'hidden' }}>
        <div ref={canvasRef} style={{ width: 'max-content', transform: `scale(${fit.scale})`, transformOrigin: 'left top' }}>
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }} aria-hidden="true">
        {/* Ім'я анімації унікальне для файлу — @keyframes глобальні в DOM,
            але sldFlow ніде більше в кабінеті не використовується. */}
        <style>{'@keyframes sldFlow { to { stroke-dashoffset: -24; } }'}</style>
        {paths.map((p, i) => {
          // Гілки «горять» одразу, без наведення — так однолінійна схема
          // читається як жива система під напругою. Наведення на картку лише
          // підсилює ЇЇ гілку (яскравіше світіння, товща лінія), а не вмикає
          // світіння з нуля — решта не гасне, а просто лишається базовою.
          const boosted = active === p.owner;
          // Було scada.accent (брендовий червоний) — за проханням підсвітка
          // не має бути червоною; бурштиновий (--warn) той самий колір, що й
          // "увага/виділення" по всьому кабінету (§3), без нового відтінку.
          const color = boosted ? scada.state.warning : scada.state.ok;
          return (
            <g key={i}>
              {/* Базова лінія зі світінням — та сама техніка, що в референсі
                  (drop-shadow навколо стрічки), лише кольором кабінету:
                  зелений = під напругою (§3, генерація/норма), брендовий
                  accent — коли гілка вибрана курсором. */}
              <path d={p.d} fill="none" stroke={color} strokeWidth={boosted ? 2.6 : 1.8} strokeLinecap="round"
                style={{
                  filter: `drop-shadow(0 0 ${boosted ? 8 : 4.5}px ${color})`,
                  transition: 'stroke .18s ease, stroke-width .18s ease, filter .18s ease',
                }} />
              {/* Біжучий пунктир — «потік» живлення, постійно на всіх гілках;
                  на вибраній — швидший і яскравіший. */}
              <path d={p.d} fill="none" stroke="#fff" strokeOpacity={boosted ? 0.9 : 0.55} strokeWidth={1.4}
                strokeLinecap="round" strokeDasharray="3 13"
                style={{ animation: `sldFlow ${boosted ? 0.7 : 1.4}s linear infinite` }} />
            </g>
          );
        })}
      </svg>

      {/* Компактна розкладка: КТП і його інвертори — КОЛОНКА (інвертор
          зверху, КТП знизу), колонки стоять поруч у РЯДОК зліва направо —
          так само компактно, як була вертикальна схема, лише розвернуто на
          90°. Точка приєднання — одна картка праворуч від усіх колонок,
          вирівняна по центру (align-items:center) всієї групи; порожнього
          місця в середині більше немає, бо хаб стоїть впритул до колонок. */}
      {/* paddingBottom тут, а не на зовнішньому treeRef: висота ЦЬОГО
          рядка (усередині canvasRef) - те, що реально бере scrollHeight для
          розрахунку fit.h/масштабу. Якщо резерв під шину лежить ЗОВНІ
          вимірюваного елемента, висота недооцінюється і overflow:hidden
          обрізає низ схеми (шинну горизонталь і хвіст лінії в хаб). */}
      <div className="flex items-center" style={{ position: 'relative', zIndex: 1, gap: 40, paddingBottom: 48 }}>
        <div className="flex items-end" style={{ gap: 20 }}>
          {detail.ktps.map((k) => {
            const kids = detail.inverters.filter((inv) => inv.ktpId === k.id);
            const working = kids.filter((inv) => inv.status !== 'offline' && inv.status !== 'alarm').length;
            const sumKw = kids.reduce((s, inv) => s + inv.pacKw, 0);
            const grpState: OpState = kids.some((inv) => inv.status === 'alarm') ? 'warning' : k.status;
            const groupLoad = kids.length > 0 ? (working / kids.length) * 100 : undefined;
            return (
              <div key={k.id} className="flex flex-col items-center" style={{ gap: 16 }}
                onPointerEnter={() => setActive(k.id)} onPointerLeave={() => setActive(null)}>
                <div ref={setNodeRef(`${k.id}-inv`)}>
                  <Node title={i18n.t('ak_scada2_inverters')} state={grpState} icon="ri-cpu-line"
                    lines={[
                      { k: i18n.t('ak_scada2_running_3'), v: `${working}/${kids.length}` },
                      { k: i18n.t('ak_scada2_power'), v: fmtKw(sumKw) },
                    ]}
                    load={groupLoad} />
                </div>
                <div ref={setNodeRef(k.id)}>
                  <Node title={k.name} state={k.status} icon="ri-archive-2-line"
                    lines={[
                      { k: i18n.t('ak_scada2_voltage_u'), v: `${k.voltageKv} ${i18n.t('ak_scada2_kv')}` },
                      { k: i18n.t('ak_scada2_current_i'), v: `${k.currentA} ${i18n.t('ak_scada2_a')}` },
                      { k: i18n.t('ak_scada2_temperature_t'), v: `${k.tempC} °C` },
                    ]} />
                </div>
              </div>
            );
          })}
          {cfg.hasBess && t.bess && (
            // БЕЗ окремого КТП - той самий рівень, що й ряд КТП знизу
            // (items-end на батьківському ряду вирівнює його по нижньому краю).
            <div ref={setNodeRef('bess')}
              onPointerEnter={() => setActive('bess')} onPointerLeave={() => setActive(null)}>
              <Node title={i18n.t('ak_scada2_bess')} state={t.bess.mode === 'fault' ? 'alarm' : 'ok'} icon="ri-battery-2-charge-line"
                big={{ value: `${t.bess.socPct}`, unit: '% SOC' }}
                lines={[
                  { k: i18n.t('ak_scada2_power'), v: `${Math.abs(t.bess.powerKw)} ${i18n.t('ak_scada2_kw')}` },
                  { k: i18n.t('ak_scada2_mode'), v: t.bess.mode === 'charging' ? i18n.t('ak_scada2_charging') : t.bess.mode === 'discharging' ? i18n.t('ak_scada2_discharging') : i18n.t('ak_scada2_standby_2') },
                ]} />
            </div>
          )}
        </div>

        <div ref={hubRef} onPointerEnter={() => setActive(null)}>
          <Node title={i18n.t('ak_scada2_point_of_connection')} state={t.state === 'offline' ? 'offline' : 'ok'} icon="ri-plug-line"
            big={{ value: bigVal, unit: bigUnit }}
            lines={[
              { k: i18n.t('ak_scada2_limit'), v: t.activeLimitKw != null ? fmtKw(t.activeLimitKw) : '—' },
              { k: i18n.t('ak_scada2_frequency'), v: `${t.freqHz} ${i18n.t('ak_scada2_hz')}` },
            ]} />
        </div>
      </div>
        </div>
      </div>
    </div>
  );
}

// ─── Inverter matrix ─────────────────────────────────────────────────────────
export function InverterMatrix({ detail }: { detail: StationDetail }) {
  const [sel, setSel] = useState<InverterUnit | null>(null);
  return (
    <div>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))' }}>
        {detail.inverters.map((inv) => {
          const c = stateColor(inv.status);
          return (
            <button key={inv.id} onClick={() => setSel(inv)}
              style={{ textAlign: 'left', background: scada.panel2, border: `1px solid ${inv.id === sel?.id ? scada.accent : c + '40'}`, borderLeft: `3px solid ${c}`, borderRadius: 8, padding: '7px 9px', cursor: 'pointer' }}>
              <div className="flex items-center justify-between">
                <span style={{ color: scada.text, fontSize: 12, fontWeight: 600 }}>{inv.id}</span>
                {dot(inv.status)}
              </div>
              <div style={{ color: scada.textDim, fontSize: 11, marginTop: 2 }}>{fmtKw(inv.pacKw)}</div>
              <div className="flex items-center justify-between" style={{ marginTop: 2 }}>
                <span style={{ color: scada.textMuted, fontSize: 10 }}>{inv.tempC ? `${inv.tempC}°C` : '—'}</span>
                <span style={{ color: inv.stringsFault ? scada.state.warning : scada.textMuted, fontSize: 10 }}>
                  {i18n.t('ak_scada2_strings', { a: inv.strings - inv.stringsFault, b: inv.strings })}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {sel && <StringGrid inv={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

// ─── Per-inverter string grid (ATS PV-string style) ──────────────────────────
function StringGrid({ inv, onClose }: { inv: InverterUnit; onClose: () => void }) {
  return (
    <div style={{ marginTop: 14, background: scada.panel2, border: `1px solid ${scada.border}`, borderRadius: 10, padding: 12 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
        <span style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>
          {i18n.t('ak_scada2_strings_ok', { id: inv.id, a: inv.strings - inv.stringsFault, b: inv.strings })}
        </span>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: scada.textMuted, cursor: 'pointer', fontSize: 16 }}><i className="ri-close-line" /></button>
      </div>
      <div className="grid gap-1.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(58px, 1fr))' }}>
        {Array.from({ length: inv.strings }).map((_, i) => {
          const fault = i >= inv.strings - inv.stringsFault;
          const c = inv.status === 'offline' ? scada.state.offline : fault ? scada.state.alarm : scada.state.ok;
          return (
            <div key={i} style={{ background: `${c}1A`, border: `1px solid ${c}55`, borderRadius: 6, padding: '5px 4px', textAlign: 'center' }}>
              <div style={{ color: scada.textDim, fontSize: 9 }}>S{String(i + 1).padStart(2, '0')}</div>
              <div style={{ color: c, fontSize: 10, fontWeight: 600 }}>{inv.status === 'offline' ? '—' : fault ? 'AVR' : `${(7 + Math.random()).toFixed(1)}A`}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

